import { useState } from "react";
import { motion } from "framer-motion";
import { Check, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const STATUE =
  "https://images.unsplash.com/photo-1651042733553-91ab334667d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Cercle() {
  const { content } = useSite();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const cercle = content?.cercle || { title: "Rejoignez une communauté d'éveil", benefits: [] };
  const wa = content ? `https://wa.me/${content.whatsapp}` : "#";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/cercle/join", { email, name });
      toast.success(data.message);
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="cercle-page" className="pt-32 pb-16 px-5 max-w-7xl mx-auto">
      <div className="grid gap-12 lg:grid-cols-2 items-center">
        <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-4">Le Cercle</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-8 leading-tight">{cercle.title}</h1>
          <p className="text-amber-200/70 font-mono-accent text-sm uppercase tracking-widest mb-5">Ce que vous y trouverez</p>
          <ul className="space-y-4 mb-10">
            {cercle.benefits.map((b, i) => (
              <li key={b} className="flex items-start gap-3 text-slate-300">
                <span className="mt-1 w-5 h-5 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <Check size={12} className="text-amber-300" />
                </span>
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>

          <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8" data-testid="cercle-form">
            <h3 className="font-serif text-2xl text-amber-100 mb-2">Rejoindre le Cercle</h3>
            <p className="text-slate-400 text-sm mb-5">Laissez votre email pour recevoir enseignements et invitations.</p>
            <div className="flex flex-col gap-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre prénom (optionnel)"
                data-testid="cercle-name-input"
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60"
              />
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre email"
                data-testid="cercle-email-input"
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="cercle-submit-button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Rejoindre le Cercle"}
              </button>
            </div>
            <a href={wa} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-emerald-300 text-sm hover:text-emerald-200">
              <MessageCircle size={16} /> Parler sur WhatsApp
            </a>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="relative rounded-3xl overflow-hidden h-[420px] lg:h-[620px]"
        >
          <img src={STATUE} alt="Méditation" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-transparent to-transparent" />
        </motion.div>
      </div>
    </div>
  );
}
